import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import MultipleProgressChart, { MultipleProgressChartData } from '../charts/MultipleProgressChart';
import { GetSalesInsightsGroupedRequest, SalesInsightsGroupedResponse, useSalesInsightsGrouped } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';


const SalesByLocation = () => {
    const { t } = useTranslation();
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const [multipleChartData, setMultipleChartData] = useState<MultipleProgressChartData[]>([]);
    const [salesByLocationSum, setSalesByLocationSum] = useState(0);
    const { refreshDate, companyConfig } = useContext(DashboardContext);


    const [params, setParams] = useState<GetSalesInsightsGroupedRequest>({
        groupBy: 'location',
        fields: 'totalSales',
        startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id
    });

    const {
        data: salesInsightsGrouped,
        isLoading,
        isError,
        refetch: fetchSalesByLocation
    } = useSalesInsightsGrouped<SalesInsightsGroupedResponse>(params, 'sales-insights-grouped-by-location');

    useEffect(() => {
        if (salesInsightsGrouped?.salesInsightsGrouped) {
            let sum = 0;
            setMultipleChartData(
                salesInsightsGrouped.salesInsightsGrouped.map(
                    (salesInsightsGroupedObject) => {
                        sum += salesInsightsGroupedObject.salesInsights.totalSalesIncTax || 0;
                        return {
                            key: salesInsightsGroupedObject.key,
                            value: salesInsightsGroupedObject.salesInsights.totalSalesIncTax || 0
                        }
                    })
            )
            setSalesByLocationSum(sum);
        }
    }, [salesInsightsGrouped]);

    useEffect(() => {
        setParams(prev => ({
            ...prev,
            startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id,
        }));
    }, [filters, dateRangeConfig, companyConfig?.locations]);

    useEffect(() => {
        fetchSalesByLocation();
    }, [params, fetchSalesByLocation]);
    
    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesByLocation();
        }
    }, [refreshDate, fetchSalesByLocation]);


    return (
        <Box>
            <MultipleProgressChart
                isError={isError}
                title={t("screens.dashboarding.charts.salesByLocation")}
                currency={salesInsightsGrouped?.currency}
                description={t("screens.dashboarding.charts.includingTax")}
                sum={salesByLocationSum}
                isLoading={isLoading}
                data={multipleChartData}
                textLink={t("screens.dashboarding.charts.salesOverview")}
                linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByLocation.aspx`}
            />
        </Box>
    )
}

export default SalesByLocation;
